import { useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { createColumnHelper, type SortingState } from "@tanstack/react-table";
import { Plus, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DataTable,
  DataTableSkeleton,
  dataTableFeatures,
} from "@/components/ui/data-table";
import {
  naarZoekparameters,
  type Filterdescriptor,
  type Filterstand,
} from "@/components/ui/data-table-filter";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { exportHref, isProblem, PROBLEM_TYPES } from "@jbnl/api-client";
import { Pager } from "@/components/ui/pager";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { FormActions, Toolbar } from "@jbnl/shared-ui";
import { ListDetail } from "@/components/ListDetail";
import { FULL_PAGE_SIZE } from "@/lib/paging";
import { useFilterStand } from "@/lib/useFilterStand";
import { Beschikbaarheden } from "@/features/wedstrijddag-deelname/Beschikbaarheden";
import { Inschrijvingen } from "@/features/wedstrijddag-deelname/Inschrijvingen";
import { Banen } from "./Banen";
import { Boekjegegevens } from "./Boekjegegevens";
import { DuplicaatWaarschuwingDialog } from "./DuplicaatWaarschuwing";
import { Promotieplaatsen } from "./Promotieplaatsen";
import { Speelschema } from "./Speelschema";
import { WedstrijddagForm, type DagWaarden } from "./WedstrijddagForm";
import { buitenSeizoen, datumKort, datumLang } from "./datum";
import {
  useBeheerKorpsen,
  useCreateWedstrijddag,
  useDeleteWedstrijddag,
  useDuplicaatWaarschuwing,
  useNiveaus,
  useUpdateWedstrijddag,
  useWedstrijddagen,
  useWedstrijddagenPaged,
  type Korps,
  type Meldadres,
  type Seizoen,
  type Wedstrijddag,
} from "./useWedstrijden";

const helper = createColumnHelper<typeof dataTableFeatures, Wedstrijddag>();

/** The `sortBy` value the API understands for each sortable column. */
const SORT_BY: Record<string, string> = {
  organiserendKorpsName: "korps",
  niveauName: "niveau",
};

/**
 * The accessor returns `dd-MM-yyyy` so the search box matches the column, but that sorts 01-06
 * before 16-05. The comparison reads the ISO date underneath instead.
 */
const opDatum = (
  a: { original: { date: string } },
  b: { original: { date: string } },
) => a.original.date.localeCompare(b.original.date);

/** Maps the table's sort state to the `sortBy`/`sortDesc` query params `useWedstrijddagenPaged` expects. */
function sorteerParams(sort: SortingState[number] | undefined) {
  return { sortBy: sort && SORT_BY[sort.id], sortDesc: sort?.desc ?? false };
}

/** The duplicate refusal is shown as a dialog instead, not repeated as plain text below the button. */
function zichtbareFout(error: Error | null) {
  return isProblem(error, PROBLEM_TYPES.wedstrijddagMogelijkDuplicaat)
    ? null
    : error;
}

const kolommen = (t: TFunction<"wedstrijden">) =>
  helper.columns([
    helper.accessor((dag) => datumKort(dag.date), {
      id: "datum",
      header: t("dagen.datum"),
      sortFn: opDatum,
      // Nowrap: the list pane is the narrow half of the screen, and a date broken over two
      // lines reads as two dates.
      cell: (info) => (
        <span className="font-medium whitespace-nowrap">{info.getValue()}</span>
      ),
    }),
    // Said out loud rather than left blank: an empty cell reads as a korps whose name failed to
    // load, and a dag with no host yet is the ordinary state of a calendar JBNL has only just
    // fixed.
    helper.accessor(
      (dag) => dag.organiserendKorpsName ?? t("dagen.nogNietBekend"),
      {
        id: "organiserendKorpsName",
        header: t("dagen.organiserendKorps"),
        cell: (info) =>
          info.row.original.organiserendKorpsName ? (
            info.getValue()
          ) : (
            <span className="text-muted-foreground">
              {t("dagen.nogNietBekend")}
            </span>
          ),
      },
    ),
    helper.accessor("niveauName", { header: t("dagen.niveau") }),
  ]);

/** The seizoen is fixed by the URL: a switcher here would be a second way to say the same thing. */
export function Wedstrijddagen({
  seizoen,
  dagId,
  onKies,
  tab,
  onKiesTab,
  pagina,
  onPaginaChange,
}: {
  seizoen: Seizoen;
  dagId: string | undefined;
  onKies: (id?: string) => void;
  /** Which section of the detail pane is open, or `undefined` for the first (ADR-0064). */
  tab: string | undefined;
  onKiesTab: (tab: string) => void;
  pagina: number | undefined;
  onPaginaChange: (pagina: number) => void;
}) {
  const { t } = useTranslation("wedstrijden");
  // Finds the selected dag for the detail pane - independent of the list's own page, so
  // choosing one on page 1 and then paging the list to page 2 does not lose the detail pane.
  const { data: dagen } = useWedstrijddagen(seizoen.id);
  const { data: korpsen } = useBeheerKorpsen();
  const { data: niveaus } = useNiveaus();
  const dag = dagen?.find((d) => d.id === dagId);

  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const { sortBy, sortDesc } = sorteerParams(sorting[0]);

  // Server-driven, so neither descriptor carries `past` and the facets show no counts: this
  // list is one page of an answer the server already narrowed (#192).
  const descriptors = useMemo<Filterdescriptor<Wedstrijddag>[]>(
    () => [
      {
        id: "niveau",
        label: t("dagen.filterNiveau"),
        kind: "keuze",
        opties: (niveaus ?? []).map((niveau) => ({
          waarde: niveau.id,
          label: niveau.name,
        })),
      },
      { id: "datum", label: t("dagen.filterDatum"), kind: "periode" },
    ],
    [niveaus, t],
  );
  // Reuses the deelname tabs' hook: both live on this same route's search params, and the hook
  // cares only about the descriptor ids, not which screen they belong to.
  const { stand, onStand } = useFilterStand(descriptors, "/wedstrijddagen");
  const zoekparameters = naarZoekparameters(stand);
  const niveauId = zoekparameters.niveau as string | undefined;
  const datumVan = zoekparameters.datumVan as string | undefined;
  const datumTot = zoekparameters.datumTot as string | undefined;

  const { data: page, isPending } = useWedstrijddagenPaged(
    seizoen.id,
    pagina,
    FULL_PAGE_SIZE,
    search,
    sortBy,
    sortDesc,
    niveauId,
    datumVan,
    datumTot,
  );

  const columns = kolommen(t);
  const korpsenLijst = korpsen ?? [];

  // A search narrows the set the server counts, so page 2 of the old set would otherwise land
  // past the end of the new, shorter one.
  function onZoekChange(waarde: string) {
    setSearch(waarde);
    onPaginaChange(1);
  }

  return (
    <>
      <Toolbar>
        <NieuweWedstrijddag
          seizoen={seizoen}
          korpsen={korpsenLijst}
          onAangemaakt={onKies}
        />
      </Toolbar>

      <ListDetail
        selected={Boolean(dag)}
        backLabel={t("dagen.terug")}
        onBack={() => onKies()}
        list={
          <WedstrijddagenLijst
            exportLink={exportHref(
              "/api/beheer/seizoenen/{seizoenId}/wedstrijddagen/export",
              {
                path: { seizoenId: seizoen.id },
                query: {
                  search,
                  sortBy,
                  sortDescending: sortDesc,
                  niveauId,
                  datumVan,
                  datumTot,
                },
              },
            )}
            t={t}
            columns={columns}
            isPending={isPending}
            page={page}
            dag={dag}
            onKies={onKies}
            sorting={sorting}
            onSortingChange={setSorting}
            search={search}
            onZoekChange={onZoekChange}
            onPaginaChange={onPaginaChange}
            descriptors={descriptors}
            stand={stand}
            onStand={onStand}
          />
        }
        detail={
          dag ? (
            // Keyed on the id: the form inside is state seeded from the record, so choosing
            // another dag resets it by remounting rather than by an effect that would also fire
            // on every background refetch.
            <WedstrijddagDetail
              key={dag.id}
              seizoen={seizoen}
              korpsen={korpsenLijst}
              dag={dag}
              dagen={dagen ?? []}
              tab={tab}
              onKiesTab={onKiesTab}
              onVerwijderd={() => onKies()}
            />
          ) : (
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">
                  {t("dagen.kies")}
                </p>
              </CardContent>
            </Card>
          )
        }
      />
    </>
  );
}

/** The list pane: a loading skeleton until the page arrives, then the table and its pager. */
function WedstrijddagenLijst({
  t,
  columns,
  isPending,
  page,
  dag,
  onKies,
  sorting,
  onSortingChange,
  search,
  onZoekChange,
  onPaginaChange,
  descriptors,
  stand,
  onStand,
  exportLink,
}: {
  t: TFunction<"wedstrijden">;
  columns: ReturnType<typeof kolommen>;
  isPending: boolean;
  page: ReturnType<typeof useWedstrijddagenPaged>["data"];
  dag: Wedstrijddag | undefined;
  onKies: (id?: string) => void;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  search: string;
  onZoekChange: (waarde: string) => void;
  onPaginaChange: (pagina: number) => void;
  descriptors: Filterdescriptor<Wedstrijddag>[];
  stand: Filterstand;
  onStand: (stand: Filterstand) => void;
  exportLink: string;
}) {
  return (
    <Card>
      {/* No CardHeader: the page title above both panes already says "Wedstrijddagen". */}
      <CardContent className="space-y-4 pt-6">
        {isPending || !page ? (
          <DataTableSkeleton
            label={t("dagen.label")}
            columns={columns}
            rows={6}
            search
          />
        ) : (
          <>
            <DataTable
              label={t("dagen.label")}
              data={page.items}
              columns={columns}
              getRowId={(row) => row.id}
              selectedId={dag?.id}
              onSelect={onKies}
              searchPlaceholder={t("dagen.zoek")}
              empty={t("dagen.leeg")}
              exportHref={exportLink}
              serverDriven={{
                sorting,
                onSortingChange,
                globalFilter: search,
                onGlobalFilterChange: onZoekChange,
              }}
              filters={{
                descriptors,
                stand,
                // Same reason as the search box above: a filter narrows what the server
                // counts, so the old page can land past the end of the new, shorter one.
                onStand: (nieuw) => {
                  onStand(nieuw);
                  onPaginaChange(1);
                },
                toonTelling: false,
              }}
            />
            <Pager
              page={page.page}
              pageSize={page.pageSize}
              totalCount={page.totalCount}
              onPageChange={onPaginaChange}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** A modal, like the seizoen's. Widest of the two, because the form carries a korpsen table. */
function NieuweWedstrijddag({
  seizoen,
  korpsen,
  onAangemaakt,
}: {
  seizoen: Seizoen;
  korpsen: Korps[];
  onAangemaakt: (id: string) => void;
}) {
  const create = useCreateWedstrijddag(seizoen.id);
  const duplicaat = useDuplicaatWaarschuwing<DagWaarden>();
  const [open, setOpen] = useState(false);

  function wissel(volgende: boolean) {
    setOpen(volgende);
    if (!volgende) create.reset();
  }

  function aangemaakt(nieuw: { id: string }) {
    wissel(false);
    onAangemaakt(nieuw.id);
  }

  return (
    <>
      <Dialog open={open} onOpenChange={wissel}>
        <NieuweWedstrijddagKnop />
        <NieuweWedstrijddagInhoud
          open={open}
          seizoen={seizoen}
          korpsen={korpsen}
          create={create}
          duplicaat={duplicaat}
          onAangemaakt={aangemaakt}
        />
      </Dialog>

      <DuplicaatWaarschuwingDialog
        bericht={duplicaat.bericht}
        onOpenChange={(open) => !open && duplicaat.sluit()}
        onDoorgaan={() =>
          create.mutate(duplicaat.doorgaan(), { onSuccess: aangemaakt })
        }
      />
    </>
  );
}

function NieuweWedstrijddagKnop() {
  const { t } = useTranslation("wedstrijden");
  return (
    <DialogTrigger asChild>
      <Button>
        <Plus />
        {t("dagen.toevoegen")}
      </Button>
    </DialogTrigger>
  );
}

/** The dialog's body: kept out of `NieuweWedstrijddag` so the header and form don't stack the
 * JSX four levels deep on top of the dialog and its trigger. */
function NieuweWedstrijddagInhoud({
  open,
  seizoen,
  korpsen,
  create,
  duplicaat,
  onAangemaakt,
}: {
  open: boolean;
  seizoen: Seizoen;
  korpsen: Korps[];
  create: ReturnType<typeof useCreateWedstrijddag>;
  duplicaat: ReturnType<typeof useDuplicaatWaarschuwing<DagWaarden>>;
  onAangemaakt: (nieuw: { id: string }) => void;
}) {
  const { t } = useTranslation("wedstrijden");

  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>{t("dagen.toevoegen")}</DialogTitle>
        <DialogDescription>
          {t("dagen.aanSeizoen", { naam: seizoen.name })}
        </DialogDescription>
      </DialogHeader>

      {/* Keyed on the open state, so every visit starts on an empty form. */}
      <WedstrijddagForm
        key={String(open)}
        id="nieuwe-dag"
        seizoen={seizoen}
        korpsen={korpsen}
        submitLabel={t("dagen.submitToevoegen")}
        pending={create.isPending}
        error={zichtbareFout(create.error)}
        onSubmit={(waarden) =>
          create.mutate(waarden, {
            onSuccess: onAangemaakt,
            onError: (error) => duplicaat.vangOp(waarden, error),
          })
        }
      />
    </DialogContent>
  );
}

/** Banen first: nothing hangs off a dag without them, and an unknown `?tab=` falls back here (ADR-0064). */
// Ploegen and Ondersteuners last: the four before them are the dag a beheerder builds, and these
// two are what came back - the korps axis and the persoon axis of the same question.
const TABS = [
  "banen",
  "boekje",
  "promoties",
  "speelschema",
  "ploegen",
  "ondersteuners",
] as const;

type Tab = (typeof TABS)[number];

function WedstrijddagDetail({
  seizoen,
  korpsen,
  dag,
  dagen,
  tab,
  onKiesTab,
  onVerwijderd,
}: {
  seizoen: Seizoen;
  korpsen: Korps[];
  dag: Wedstrijddag;
  /** Every dag of the seizoen, which is what a promotieplaats picks its bestemming from. */
  dagen: Wedstrijddag[];
  tab: string | undefined;
  onKiesTab: (tab: string) => void;
  onVerwijderd: () => void;
}) {
  const { t } = useTranslation(["wedstrijden", "common"]);
  const update = useUpdateWedstrijddag(seizoen.id);
  const remove = useDeleteWedstrijddag(seizoen.id);
  const { data: niveaus } = useNiveaus();
  const duplicaat = useDuplicaatWaarschuwing<{
    id: string;
    date: string;
    niveauId: string;
    organiserendKorpsId: string | null;
    meldadres: Meldadres | null;
  }>();

  // A stale or hand-typed section is the first tab rather than an empty pane, the way `pagina`
  // is page 1 rather than nothing chosen.
  const openTab = TABS.find((section) => section === tab) ?? TABS[0];

  // Only where the section is a collection. The boekje is five free-text fields, and this dag's
  // speelschema is a grid per baan rather than the single list one klasse's standaard is.
  const counts: Partial<Record<Tab, number>> = {
    banen: dag.wedstrijdbanen.length,
    promoties: dag.wedstrijdbanen.reduce(
      (total, baan) => total + baan.promotieplaatsen.length,
      0,
    ),
  };

  function label(section: Tab) {
    const text = t(`tabs.${section}`);
    const aantal = counts[section];
    return aantal === undefined
      ? text
      : t("common:tabs.metAantal", { label: text, aantal });
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <WedstrijddagKop dag={dag} seizoen={seizoen} t={t} />
        </CardHeader>
        <CardContent>
          <WedstrijddagForm
            id="dag"
            seizoen={seizoen}
            korpsen={korpsen}
            dag={dag}
            submitLabel={t("common:actions.save")}
            pending={update.isPending}
            error={zichtbareFout(update.error)}
            // Sent as the form has it, the meldadres included. It used to be backfilled from
            // the dag here, because the edit path refused a null one; it defaults from the
            // organiserend korps now, exactly as the create path does.
            //
            // The four fields are named rather than spread, so the three the create path adds -
            // the banen and the wedstrijdleiders - are visibly dropped here instead of quietly
            // riding along in a PUT that has nowhere to put them (ADR-0030).
            onSubmit={({ date, niveauId, organiserendKorpsId, meldadres }) => {
              const waarden = {
                id: dag.id,
                date,
                niveauId,
                organiserendKorpsId,
                meldadres,
              };
              update.mutate(waarden, {
                onError: (error) => duplicaat.vangOp(waarden, error),
              });
            }}
          />
        </CardContent>
      </Card>

      <DuplicaatWaarschuwingDialog
        bericht={duplicaat.bericht}
        onOpenChange={(open) => !open && duplicaat.sluit()}
        onDoorgaan={() => update.mutate(duplicaat.doorgaan())}
      />

      {/* The form stays above the strip because it says which dag this is, and the delete card
          below it because a tab labelled Verwijderen is a trap (ADR-0064). */}
      <Tabs value={openTab} onValueChange={onKiesTab}>
        <TabsList aria-label={t("tabs.strip")}>
          {TABS.map((section) => (
            <TabsTrigger key={section} value={section}>
              {label(section)}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="banen">
          <Banen seizoenId={seizoen.id} dag={dag} />
        </TabsContent>

        <TabsContent value="boekje">
          <Boekjegegevens key={dag.id} seizoenId={seizoen.id} dag={dag} />
        </TabsContent>

        <TabsContent value="promoties">
          <Promotieplaatsen
            key={`promoties-${dag.id}`}
            seizoenId={seizoen.id}
            dag={dag}
            dagen={dagen}
            niveaus={niveaus}
          />
        </TabsContent>

        <TabsContent value="ploegen">
          {/* Which ploegen asked for this dag, which ruled it out, and which said nothing. Not
              the same subject as /ploegen, which is namens handelen on the korps axis: this is
              the dag's own answers, and the dag's screen is where they belong. */}
          <Card>
            <CardContent className="pt-6">
              <Inschrijvingen
                key={`ploegen-${dag.id}`}
                wedstrijddagId={dag.id}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ondersteuners">
          <Card>
            <CardContent className="pt-6">
              <Beschikbaarheden
                key={`ondersteuners-${dag.id}`}
                wedstrijddagId={dag.id}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="speelschema">
          <Speelschema seizoenId={seizoen.id} dag={dag} />
        </TabsContent>
      </Tabs>

      <WedstrijddagVerwijderCard
        dag={dag}
        remove={remove}
        onVerwijderd={onVerwijderd}
        t={t}
      />
    </div>
  );
}

/** The title plus the out-of-season warning: split off so the card header doesn't stack onto
 * the tabs and delete card below it in one long, deeply-nested return. */
function WedstrijddagKop({
  dag,
  seizoen,
  t,
}: {
  dag: Wedstrijddag;
  seizoen: Seizoen;
  t: TFunction<["wedstrijden", "common"]>;
}) {
  return (
    <>
      <CardTitle>
        {t("dagen.titel", {
          datum: datumLang(dag.date),
          korps: dag.organiserendKorpsName ?? t("dagen.nogGeenKorps"),
        })}
      </CardTitle>
      {buitenSeizoen(dag.date, seizoen.startDate, seizoen.endDate) && (
        <p className="flex items-center gap-2 text-sm">
          <TriangleAlert aria-hidden className="size-4 shrink-0" />
          {t("dagen.buitenSeizoen", { naam: seizoen.name })}
        </p>
      )}
    </>
  );
}

/** Its own card, not a tab: a tab labelled Verwijderen is a trap (ADR-0064). */
function WedstrijddagVerwijderCard({
  dag,
  remove,
  onVerwijderd,
  t,
}: {
  dag: Wedstrijddag;
  remove: ReturnType<typeof useDeleteWedstrijddag>;
  onVerwijderd: () => void;
  t: TFunction<["wedstrijden", "common"]>;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("dagen.verwijderenTitle")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {t("dagen.verwijderenUitleg")}
        </p>
        <FormActions>
          {remove.error && (
            <p className="text-sm text-destructive">{remove.error.message}</p>
          )}
          <ConfirmDelete
            trigger={
              <Button variant="destructive" disabled={remove.isPending}>
                {remove.isPending
                  ? t("common:actions.busy")
                  : t("common:actions.delete")}
              </Button>
            }
            title={t("dagen.verwijderenVraag", { datum: datumKort(dag.date) })}
            description={t("dagen.verwijderenDescription")}
            confirmLabel={t("dagen.verwijderenConfirm")}
            onConfirm={() => remove.mutate(dag.id, { onSuccess: onVerwijderd })}
          />
        </FormActions>
      </CardContent>
    </Card>
  );
}
